import * as React from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { getAgentPathPrefix, instance } from '@lib/index';
import {
  DiffStates,
  DiffStatesClass,
  DataQueryUpdateFuncArgumentConfig,
  ReferenceNode,
  DataQuery,
  Attribute,
  FrameItem,
  IntentInfo,
  CompositeInfo,
  EntityInfo,
  FrameType,
  GenericType,
  ExternalType,
  ProviderType,
  FunctionParameter,
  ParameterType
} from '@interface/index';
import { DbModal } from './db-modal';
import { NiTable, sourceRender } from '@component/index';
import { localizedString } from '@locale/index';
import EmptyLine from '@component/business-component/empty-line/empty-line';

export interface DbTableProps {
  title: string;
  frame: FrameItem;
  availableIntents: IntentInfo[];
  availableComposites: CompositeInfo[];
  availableEntities: EntityInfo[];
  genericTypes: GenericType[];
  externalTypes: ExternalType[];
  disabled?: boolean;
  tag: string;
  data: DataQuery[];
  providerType?: ProviderType;
  selectedParameters: Attribute[];
  onAdd?: () => void;
  onChange: (args: DataQueryUpdateFuncArgumentConfig) => void;
}
export interface DbTableState {
  visible: boolean;
  cur: number;
  isNew: boolean;
}

export class QueryTableComp extends React.Component<
  DbTableProps,
  DbTableState
> {
  public state: DbTableState = {
    visible: false,
    cur: -1,
    isNew: false
  };

  componentDidMount() {
    instance.on(`@addDataQuery${this.props.tag}`, (args: any) => {
      this.setState({
        cur: 0,
        visible: true,
        isNew: args && args.isNew
      });
    });
  }
  componentWillUnmount() {
    instance.off(`@addDataQuery${this.props.tag}`);
  }

  render() {
    const state = this.state;
    const props = this.props;
    const {
      title,
      frame,
      disabled = false,
      data,
      availableIntents,
      availableComposites,
      availableEntities,
      genericTypes,
      externalTypes,
      providerType
    } = this.props;
    const readonly = data[state.cur] && data[state.cur].readonly;

    return (
      <>
        {this.state.cur !== -1 && state.visible && (
          <DbModal
            disabled={disabled || readonly}
            title={title}
            frame={frame}
            providerType={providerType}
            availableIntents={availableIntents}
            availableComposites={availableComposites}
            availableEntities={availableEntities}
            genericTypes={genericTypes}
            externalTypes={externalTypes}
            data={data[state.cur]}
            visible={state.visible}
            confirm={this.handleConfirm}
            selectedParameters={props.selectedParameters}
            close={this.handleClose}
          />
        )}
        {data && data.filter(item => !item.toBeDeleted).length &&
          <NiTable
            className="db-table"
            rowKey={(record: DataQuery) => record.id || ''}
            dataSource={this.props.data}
            columns={this.getColumns()}
            rowClassName={(record: DataQuery, index: number): string =>
              DiffStatesClass[record.state || '']
            }
            onRow={(record: DataQuery, index?: number) => {

              return {
                onClick: () => {

                  if (
                    record.state !== DiffStates.DEL &&
                    record.state !== DiffStates.MASTER_CONFLICT
                  ) {
                    window.open(`${getAgentPathPrefix()}/${this.getOwnerPath()}/${frame.id}/function/${record.id}`, '_self');
                    // this.setState({
                    //   cur: index || 0,
                    //   visible: true,
                    //   isNew: false
                    // });
                  }
                }
              };
            }}
            pagination={false}
          /> || <EmptyLine text="No function yet, you can add one by select the return type" />
        }
      </>
    );
  }

  getOwnerPath() {
    const type = this.props.frame.type;
    switch (type) {
      case FrameType.intent:
        return 'intent';
      case FrameType.composite:
        return 'frame';
      case FrameType.entity:
        return 'entity';
      case FrameType.service:
        return 'service';
      default:
        return 'unknown';
    }
  }

  getColumns = () => {
    const props = this.props;

    const nameColumn = {
      // width: 300,
      title: localizedString('data_query_name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: DataQuery, index: number) => {
        return (
          <div className="table-name-area text">
            <span>{`${name}(${record.parameters.length
              ? record.parameters.map((param: FunctionParameter) =>
                `${param.type && param.type.name}${param.multiValue
                  ? '[]'
                  : ''}${param.nullable ? '?' : ''}`
              )
                .join(', ')
              : ''})`}</span>
          </div>
        );
      }
    };

    // const inputTypeColumn = {
    //   // width: '30%',
    //   title: localizedString('data_query_input_type'),
    //   dataIndex: 'parameters',
    //   render: (parameters: FunctionParameter[], record: DataQuery, index: number) => {
    //     return (
    //       <div className="table-name-area text">
    //         <span>{parameters.length
    //           ? parameters.map((param: FunctionParameter) =>
    //             `${param.type && param.type.name}${param.multiValue
    //               ? '[]'
    //               : ''}`
    //           )
    //             .join(',')
    //           : ''}
    //         </span>
    //       </div>
    //     );
    //   }
    // };

    const frameColumn = {
      // width: '30%',
      title: localizedString('data_query_frame'),
      dataIndex: 'frame',
      render: (frame: ReferenceNode, record: DataQuery, index: number) => {
        if (!frame) {
          return 'Undefined';
        }

        let name = frame.name;

        if (record.frame && record.frame.type === ParameterType.GENERIC_TYPE) {
          name = `<${name}>`;
        }

        return `${name}${record.multiValue ? '[]' : ''}${record.disableReturnNull ? '' : '?'}`;
      }
    };

    const sourceColumn = {
      width: 150,
      title: 'Source',
      dataIndex: 'inheritedFromCompositeName',
      render: (name: string, record: DataQuery, index: number) => {
        return sourceRender(record, props.frame.type);
      }
    };

    const adminColumn = {
      title: '',
      className: 'hide',
      dataIndex: 'admin',
      width: 70,
      render: (admin: any, record: DataQuery, index: number) => {
        const readonly = record.readonly;
        return (
          <div className="icon-area">
            {
              !props.disabled &&
              !record.originKey &&
              !record.inheritedFromCompositeId &&
              !readonly &&
              (
                <DeleteOutlined
                  className="delete-icon"
                  onClick={async (e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();

                    const realIndex = props.data.indexOf(record);
                    props.data.splice(realIndex, 1);

                    props.onChange({
                      nextDataQueries: props.data,
                      nextDelDataQuery: record
                    });
                  }} />
              )}
          </div>
        );
      }
    };

    return [
      nameColumn,
      // inputTypeColumn,
      frameColumn,
      sourceColumn,
      adminColumn
    ];
  };

  handleConfirm = (next: any) => {
    const state = this.state;
    const props = this.props;
    next.dirty = true;
    this.props.data[state.cur] = next;
    this.props.onChange({
      nextDataQueries: props.data
    });
    this.setState({
      visible: false,
      cur: -1
    });
  };

  handleClose = () => {
    const state = this.state;
    const props = this.props;

    if (state.isNew) {
      props.data.splice(this.state.cur, 1);
    }

    this.setState({
      visible: false,
      cur: -1
    });
  };
}
